import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./register.style.css";
import logo from "../../assets/logo.png";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

function RegisterComponent({
    formData,
    confirmPassword,
    onConfirmPasswordChange,
    passwordError,
    vatError,
    kvkError,
    onChange,
    onVatChange,
    onSubmit,
    checkboxChecked,
    onCheckboxChange,
    checkboxError,
    setCheckboxError
}) {
    const { t } = useTranslation();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!checkboxChecked) {
            setCheckboxError(t('register.termsRequired'));
            return;
        }
        onSubmit(e);
    };

    return (
        <div className="login-container register-container">
            <LanguageSwitcher variant="auth" />

            <main className="register-box">
                <form onSubmit={handleSubmit} className="register-form enhanced-register-form" noValidate>
                    <img
                        src={logo}
                        alt="Ferreira Systeembouw Logo"
                        className="login-logo"
                        loading="eager"
                    />

                    <h1 className="register-form__title text-2xl font-bold text-center mb-5">
                        {t('register.title')}
                    </h1>

                    <div className="register-form__wrapper">
                        {/* Personal Information Section */}
                        <section className="register-form__personal_info register-form__card card" aria-labelledby="personal-info-heading">
                            <h2 id="personal-info-heading" className="card-title text-xl mb-4">
                                {t('register.personalInfo')}
                            </h2>

                            <div className="form-group">
                                <label htmlFor="name" className="form-label">
                                    {t('register.fullName')}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="form-input"
                                    placeholder={t('register.name')}
                                    value={formData.name}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email" className="form-label">
                                    {t('register.email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className="form-input"
                                    placeholder={t('register.email')}
                                    value={formData.email}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="email"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password" className="form-label">
                                    {t('register.password')}
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    className="form-input"
                                    placeholder={t('register.password')}
                                    value={formData.password}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword" className="form-label">
                                    {t('register.confirmPassword')}
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    className={`form-input ${passwordError ? 'border-error' : ''}`}
                                    placeholder={t('register.confirmPassword')}
                                    value={confirmPassword}
                                    onChange={onConfirmPasswordChange}
                                    required
                                    aria-required="true"
                                    aria-invalid={passwordError ? "true" : "false"}
                                    aria-describedby={passwordError ? "password-error" : undefined}
                                    autoComplete="new-password"
                                />
                                {passwordError && (
                                    <span id="password-error" className="form-error" role="alert">
                                        {passwordError}
                                    </span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone" className="form-label">
                                    {t('register.phone')}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    className="form-input"
                                    placeholder={t('register.phone')}
                                    value={formData.phone}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="tel"
                                />
                            </div>
                        </section>

                        {/* Company Information Section */}
                        <section className="register-form__company_info register-form__card card" aria-labelledby="company-info-heading">
                            <h2 id="company-info-heading" className="card-title text-xl mb-4">
                                {t('register.companyInfo')}
                            </h2>

                            <div className="form-group">
                                <label htmlFor="kvk_number" className="form-label">
                                    {t('register.kvkNumber')}
                                </label>
                                <input
                                    type="text"
                                    id="kvk_number"
                                    name="kvk_number"
                                    className={`form-input ${kvkError ? 'border-error' : ''}`}
                                    placeholder={t('register.kvkNumber')}
                                    value={formData.kvk_number}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    aria-invalid={kvkError ? "true" : "false"}
                                    aria-describedby={kvkError ? "kvk-error" : undefined}
                                    autoComplete="organization"
                                />
                                {kvkError && (
                                    <span id="kvk-error" className="form-error" role="alert">
                                        {kvkError}
                                    </span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="company_name" className="form-label">
                                    {t('register.companyName')}
                                </label>
                                <input
                                    type="text"
                                    id="company_name"
                                    name="company_name"
                                    className="form-input"
                                    placeholder={t('register.companyName')}
                                    value={formData.company_name}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="organization-title"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="address" className="form-label">
                                    {t('register.companyAddress')}
                                </label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    className="form-input"
                                    placeholder={t('register.companyAddress')}
                                    value={formData.address}
                                    onChange={onChange}
                                    required
                                    aria-required="true"
                                    autoComplete="street-address"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="vat_number" className="form-label">
                                    {t('register.vatNumber')}
                                </label>
                                <input
                                    type="text"
                                    id="vat_number"
                                    name="vat_number"
                                    className={`form-input ${vatError ? 'border-error' : ''}`}
                                    placeholder={t('register.vatNumber')}
                                    value={formData.vat_number}
                                    onChange={onVatChange}
                                    required
                                    aria-required="true"
                                    aria-invalid={vatError ? "true" : "false"}
                                    aria-describedby={vatError ? "vat-error" : undefined}
                                    autoComplete="vat-number"
                                />
                                {vatError && (
                                    <span id="vat-error" className="form-error" role="alert">
                                        {vatError}
                                    </span>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="register-form__checkbox enhanced-register-checkbox mt-5 mb-4">
                        <label className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={checkboxChecked}
                                onChange={onCheckboxChange}
                                required
                                aria-required="true"
                                aria-invalid={checkboxError ? "true" : "false"}
                                aria-describedby={checkboxError ? "checkbox-error" : undefined}
                            />
                            <span className="text-sm">
                                {t('register.agreeToTerms')}{' '}
                                <a
                                    href="/algemene-voorwaarden"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary font-semibold"
                                >
                                    {t('register.termsAndConditions')}
                                </a>
                            </span>
                        </label>
                        {checkboxError && (
                            <span id="checkbox-error" className="form-error mt-2" role="alert">
                                {checkboxError}
                            </span>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions register-form__actions">
                        <button
                            type="submit"
                            className="btn btn-add w-full"
                            disabled={!checkboxChecked}
                            aria-label={t('register.register')}
                        >
                            {t('register.register')}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}

RegisterComponent.propTypes = {
    formData: PropTypes.object.isRequired,
    confirmPassword: PropTypes.string.isRequired,
    onConfirmPasswordChange: PropTypes.func.isRequired,
    passwordError: PropTypes.string,
    vatError: PropTypes.string,
    kvkError: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onVatChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    checkboxChecked: PropTypes.bool.isRequired,
    onCheckboxChange: PropTypes.func.isRequired,
    checkboxError: PropTypes.string,
    setCheckboxError: PropTypes.func.isRequired,
};

export default RegisterComponent;
